import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, Legend, CartesianGrid } from "recharts";
import { TrendUp, CheckCircle, Warning, Clock, FilePdf, FileCsv } from "@phosphor-icons/react";

const Kpi = ({ label, value, sub, icon, tone = "" }) => (
  <div className="border border-border bg-card p-4" data-testid={`flow-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="overline text-muted-foreground">{label}</span>
      <span className={tone}>{icon}</span>
    </div>
    <div className="font-display font-black text-3xl tracking-tighter mt-1">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

// ClaroFlow analytics panel: open vs closed trend, avg time-to-close, overdue by owner.
export default function FlowAnalytics() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    const cat = category === "all" ? "" : `&category=${category}`;
    api.get(`/flow/analytics?months=${months}${cat}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [months, category]);

  const exportReport = async (format) => {
    const cat = category === "all" ? "" : `&category=${category}`;
    try {
      const res = await api.get(`/flow/analytics/export?months=${months}&format=${format}${cat}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `claroflow_analytics_${category}.${format}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  if (!data) return <div className="p-8 text-sm text-muted-foreground" data-testid="flow-analytics-loading">Loading analytics…</div>;
  const k = data.kpis || {};
  const maxOverdue = Math.max(1, ...(data.overdue_by_assignee || []).map((o) => o.count));
  const CATS = [["all", "All programs"], ["op", "O&P"], ["cip", "CIP"], ["security", "Security"]];

  return (
    <div className="space-y-6" data-testid="flow-analytics">
      <div className="flex items-center gap-2 flex-wrap" data-testid="flow-analytics-category-filter">
        <span className="overline text-muted-foreground mr-1">Program:</span>
        {CATS.map(([k2, l]) => (
          <button key={k2} data-testid={`flow-analytics-cat-${k2}`} onClick={() => setCategory(k2)}
            className={`border px-3 py-1.5 text-xs font-semibold rounded-none ${category === k2 ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>{l}</button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="flow-export-pdf" onClick={() => exportReport("pdf")} className="border border-border hover:bg-secondary px-3 py-1.5 text-xs font-semibold rounded-none inline-flex items-center gap-1.5"><FilePdf size={14} />Export PDF</button>
          <button data-testid="flow-export-csv" onClick={() => exportReport("csv")} className="border border-border hover:bg-secondary px-3 py-1.5 text-xs font-semibold rounded-none inline-flex items-center gap-1.5"><FileCsv size={14} />Export CSV</button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Open" value={k.open ?? 0} icon={<TrendUp size={18} />} sub="active tickets" />
        <Kpi label="Closed" value={k.closed ?? 0} icon={<CheckCircle size={18} className="text-emerald-600" />} sub="terminal tickets" />
        <Kpi label="Overdue" value={k.overdue ?? 0} icon={<Warning size={18} className="text-red-600" />} sub="past due date" tone="text-red-600" />
        <Kpi label="Avg time to close" value={k.avg_time_to_close_days != null ? `${k.avg_time_to_close_days}d` : "—"} icon={<Clock size={18} />} sub="across closed tickets" />
      </div>

      <div className="border border-border bg-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold">Open vs Closed over time</h3>
          <select data-testid="flow-analytics-months" value={months} onChange={(e) => setMonths(Number(e.target.value))}
            className="border border-border bg-background text-sm px-2 py-1 rounded-none">
            <option value={3}>Last 3 months</option>
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
          </select>
        </div>
        <div style={{ width: "100%", height: 280 }} data-testid="flow-analytics-chart">
          <ResponsiveContainer>
            <BarChart data={data.trend || []} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
              <RTooltip contentStyle={{ borderRadius: 0, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="opened" name="Opened" fill="#2563eb" />
              <Bar dataKey="closed" name="Closed" fill="#16a34a" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-border bg-card p-4">
        <h3 className="font-display font-bold mb-3">Overdue tickets by assignee</h3>
        {(data.overdue_by_assignee || []).length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="flow-overdue-empty">No overdue tickets — nice and tidy.</p>
        ) : (
          <div className="space-y-2" data-testid="flow-overdue-by-assignee">
            {data.overdue_by_assignee.map((o) => (
              <div key={o.assignee} className="flex items-center gap-3" data-testid={`flow-overdue-${o.assignee.replace(/\s+/g, "-")}`}>
                <span className="w-40 shrink-0 text-sm truncate">{o.assignee}</span>
                <div className="flex-1 bg-secondary/40 h-6 relative">
                  <div className="bg-red-500 h-6" style={{ width: `${(o.count / maxOverdue) * 100}%` }} />
                </div>
                <span className="w-8 text-right text-sm font-semibold">{o.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
